import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Globe, Smartphone, BarChart } from 'lucide-react';

const Counter = ({ end, duration = 2, label, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration * 60); 

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        clearInterval(timer);
        start = end;
      }
      setCount(Math.ceil(start));
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [end, duration]);

  return (
    <div className="text-center">
      <p className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-neon-pink via-neon-purple to-neon-blue animate-bg-pan">
        {count.toLocaleString()}{suffix}
      </p>
      <p className="text-sm text-cyber-text-secondary mt-1">{label}</p>
    </div>
  );
};


const TrafficStatisticsSection = () => {
  const stats = [
    { icon: <Users className="h-10 w-10 text-neon-pink" />, value: 100000000, label: 'Impressions Monthly', suffix: "+" },
    { icon: <Globe className="h-10 w-10 text-neon-blue" />, value: 200, label: '200+ GEOs', suffix: "+" },
    { icon: <Smartphone className="h-10 w-10 text-neon-purple" />, value: 85, label: 'Mobile Traffic', suffix: "%" },
  ];

  const barChartData = [
    { label: 'Asia', value: 45, color: 'bg-neon-pink' },
    { label: 'Europe', value: 25, color: 'bg-neon-blue' },
    { label: 'N. America', value: 15, color: 'bg-neon-purple' },
    { label: 'S. America', value: 8, color: 'bg-pink-400' },
    { label: 'Africa', value: 5, color: 'bg-purple-400' },
    { label: 'Oceania', value: 2, color: 'bg-blue-400' },
  ];

  return (
    <section id="traffic-stats" className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold text-center mb-4 text-glow-pink"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          Global Traffic Insights
        </motion.h2>
        <motion.p 
          className="text-lg text-cyber-text-secondary text-center mb-12 md:mb-16 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Visualize our expansive network reach and understand the diverse global audience you can connect with.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 rounded-lg glassmorphism border border-neon-blue/30 flex flex-col items-center text-center shadow-neon-sm-blue hover:shadow-neon-md-blue transition-shadow duration-300"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div className="mb-4 p-3 rounded-full bg-cyber-surface border border-current">
                {stat.icon}
              </div>
              <Counter end={stat.value} label={stat.label} suffix={stat.suffix} />
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="p-6 md:p-8 rounded-lg glassmorphism border border-neon-purple/30 shadow-neon-sm-purple"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center mb-6">
            <BarChart className="h-8 w-8 text-neon-purple mr-3"/>
            <h3 className="text-2xl font-semibold text-glow-purple">Geo Distribution</h3>
          </div>
          <div className="space-y-3">
            {barChartData.map((bar) => (
              <div key={bar.label} className="flex items-center">
                <span className="w-28 text-sm text-cyber-text-secondary">{bar.label}</span>
                <div className="flex-1 h-6 bg-cyber-surface rounded overflow-hidden border border-white/10">
                  <motion.div
                    className={`${bar.color} h-full rounded`}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${bar.value}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                  />
                </div>
                <span className="ml-3 w-10 text-sm text-cyber-text-primary">{bar.value}%</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-cyber-text-secondary mt-4 text-center">*Illustrative data only; actual traffic varies daily.</p>
        </motion.div>
        
        {/* Placeholder for heatmap/globe - too complex for initial build */}
        <div className="text-center mt-12">

        </div>

      </div>
    </section>
  );
};

export default TrafficStatisticsSection;